/**
 * Конфиг API/OAuth по домену.
 * Один бандл для всех доменов: выбор по window.location.hostname.
 */

export type AppConfig = {
	apiUrl: string
	marketingApiUrl: string
	oauthUrl: string
	clientId: string
}

const CLIENT_ID = "6a20157cd18d13dd6c0b9b6c"

/**
 * Кастомные клиенты с нестандартной субдоменной структурой.
 * Проверяются до применения универсального алгоритма.
 */
const CUSTOM_DOMAIN_OVERRIDES: Record<string, AppConfig> = {
	"rzd-med.ru": {
		apiUrl: "https://stats-api.med-me.rzd-med.ru",
		marketingApiUrl: "https://marketing-api.med-me.rzd-med.ru",
		oauthUrl: "https://oauth.med-me.rzd-med.ru",
		clientId: CLIENT_ID,
	},
	"invitro.ru": {
		apiUrl: "https://stats-api-gbooking.invitro.ru",
		marketingApiUrl: "https://marketing-api-gbooking.invitro.ru",
		oauthUrl: "https://oauth2-gbooking.invitro.ru",
		clientId: CLIENT_ID,
	},
}

function getHostname(): string {
	if (typeof window === "undefined") return ""
	return window.location.hostname
}

function getPortSuffix(): string {
	if (typeof window === "undefined") return ""
	const port = window.location.port
	return port ? `:${port}` : ""
}

export function resolveAbsoluteUrl(url: string): string {
	if (!url.startsWith("//")) {
		return url
	}

	if (typeof window !== "undefined") {
		return `${window.location.protocol}${url}`
	}

	return `https:${url}`
}

function findCustomOverride(hostname: string): AppConfig | undefined {
	for (const [domain, config] of Object.entries(CUSTOM_DOMAIN_OVERRIDES)) {
		if (hostname === domain || hostname.endsWith(`.${domain}`)) {
			return config
		}
	}
	return undefined
}

function buildDynamicConfig(hostname: string): AppConfig {
	const parts = hostname.split(".")
	const rootDomain = parts.slice(-2).join(".")
	const envParts = parts.slice(1, -2)
	const envSegment = envParts.length > 0 ? `.${envParts.join(".")}` : ""
	const portSuffix = getPortSuffix()

	const buildUrl = (sub: string) =>
		`//${sub}${envSegment}.${rootDomain}${portSuffix}`

	return {
		apiUrl: buildUrl("stats-api"),
		marketingApiUrl: buildUrl("marketing-api"),
		oauthUrl: buildUrl("oauthv2"),
		clientId: CLIENT_ID,
	}
}

/**
 * Возвращает конфиг для текущего домена. Без сетевых запросов.
 */
export const getAppConfig = (): AppConfig => {
	const hostname = getHostname()
	return findCustomOverride(hostname) ?? buildDynamicConfig(hostname)
}

export const getOAuthRedirectUri = (): string => {
	if (typeof window === "undefined") {
		return "/"
	}
	return window.location.origin + window.location.pathname
}

export const buildOAuthAuthorizeUrl = (config?: AppConfig): string => {
	const resolved = config ?? getAppConfig()
	const params = new URLSearchParams({
		type: "web_server",
		redirect_uri: getOAuthRedirectUri(),
		response_type: "code",
		client_id: resolved.clientId,
	})
	return `${resolved.oauthUrl}/authorize?${params.toString()}`
}
